package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.AgentConnectionDto
import dev.lingvo.shared.SpendApprovalsDto
import dev.lingvo.shared.TokenScope
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.runWorkspaceAction
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * Agent connections.
 *
 * The claim is that a fleet may touch this workspace unsupervised, and this page's job is to answer
 * "what is the robot doing" at a glance — which is a daily operational question, not a settings one.
 *
 * Creating lives on its own guided route because **the defaults are the security model** and a form
 * on a list page invites skipping them. Everything destructive lives on the connection's own page,
 * so a mis-aimed click on a row cannot revoke anything.
 */
@Page("/app/settings/agents")
@Layout(".components.layouts.AppLayout")
@Composable
fun AgentsPage() {
    AgentsSurface(
        workspaceContext = WorkspaceRequestContext.current(),
        readAgents = ApiClient::agents,
        readApprovals = ApiClient::spendApprovals,
    )
}

/**
 * The page's body, with its two reads as parameters.
 *
 * Split out of [AgentsPage] for one reason: which surface carries the page's one primary action
 * depends on whether the fleet is empty, and that is a fact only a loaded list has. A test that
 * cannot supply the list cannot see either arrangement, and this page had shipped with both of them
 * on screen at once.
 */
@Composable
internal fun AgentsSurface(
    workspaceContext: WorkspaceRequestContext?,
    readAgents: suspend (workspaceSlug: String) -> List<AgentConnectionDto>,
    readApprovals: suspend (workspaceSlug: String) -> SpendApprovalsDto,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
) {
    val ctx = rememberPageContext()

    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }
    var busy by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(false) }
    var message by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }
    var confirmingPauseAll by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(false) }
    // **One primary per surface, and the empty state is a surface.** The header offered "Connect an
    // agent" and so did the empty state under it — two answers to "what is the thing to do here",
    // 300px apart, for the same destination. While the empty state is on screen it owns the action;
    // the header takes it back the moment there is a fleet to act on. False until the first list
    // lands, so the button is never drawn and then taken away by a load that was already running.
    var headerCarriesPrimary by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(false) }

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            PageHeader(
                t("app.settings.agents.agents", "Agents"),
                t("app.settings.agents.credentials_for_the_tools_and_fleets", "Credentials for the tools and fleets that work here without a person."),
                mark = { LBotIcon() },
                actions = {
                    // Moved out of the row of links below, where it sat beside two navigation links
                    // and read as a third one. The page has one primary action and this is it —
                    // except while the empty state below is carrying it.
                    if ((workspaceContext == null || workspaceContext.canManageAgents) && headerCarriesPrimary) {
                        LButton(
                            t("app.settings.agents.connect_an_agent", "Connect an agent"),
                            { ctx.router.navigateTo("/app/settings/agents/new") },
                            leadingIcon = { LPlusIcon() },
                        )
                    }
                },
            )

            AgentsProtectedReadLeaf(
                context = workspaceContext,
                refreshKey = refreshKey,
                currentContext = currentContext,
                agents = readAgents,
                approvals = readApprovals,
            ) { agentClaim, agentReload, approvalReload ->
                val state = agentReload.state
                val approvals = approvalReload.state
                // Written from an effect rather than during composition: the header is composed
                // above this leaf, so the arrangement takes effect on the recomposition after the
                // list lands.
                //
                // False while the list is still loading, not true — a header that draws the primary
                // during the skeleton and takes it away when an empty workspace answers is a
                // control that appears and vanishes under the reader's cursor. A read that *failed*
                // does put it back: there is no empty state on screen to carry it, and "connect an
                // agent" must not become unreachable because a list request dropped.
                val loadedAgents = (state as? Async.Success)?.value
                val readFailed = state is Async.Failure
                LaunchedEffect(loadedAgents?.isEmpty(), readFailed) {
                    headerCarriesPrimary = readFailed || loadedAgents?.isNotEmpty() == true
                }
                // Beside the fleet that is still on screen: pausing one connection reloads the
                // list underneath it, and a reload that fails must not blank what it replaced.
                (agentReload.refreshError ?: approvalReload.refreshError)?.let { failure ->
                    LAlert(
                        t("app.settings.agents.showing_the_last_list_that_loaded", "Showing the last list that loaded"),
                        failure.readable(),
                        variant = LAlertVariant.WARNING,
                        dense = true,
                        live = true,
                    )
                }
                // Never present at load: it only ever appears because somebody just pressed something.
                message?.let {
                    LAlert(
                        t("app.settings.agents.that_didnt_work", "That didn't work"),
                        it,
                        variant = LAlertVariant.WARNING,
                        live = true,
                    )
                }

                // **"Spend requests" is a tab on the section nav above this page.** Repeating it
                // here as a link was the same word, for the same destination, 150px lower — and a
                // reader who sees a word twice looks for the difference between them. What the tab
                // cannot say is how many requests are waiting, or that the queue could not be read
                // at all, so the link stays only while it carries one of those two facts.
                val approvalNote = when (approvals) {
                    // A failed read used to fall through to the bare label, so a queue nobody could
                    // read looked exactly like an empty one. Understating money an agent is stuck
                    // waiting on is the one direction this number must not fail in.
                    is Async.Failure ->
                        t("app.settings.agents.spend_requests_couldnt_check", "Spend requests (couldn't check)")
                    is Async.Success -> {
                        val waiting = approvals.value?.approvals?.size ?: 0
                        if (waiting > 0) {
                            tPlural(
                                "app.settings.agents.spend_requests_waiting",
                                waiting,
                                "Spend requests ({count} waiting)",
                                "Spend requests ({count} waiting)",
                            )
                        } else {
                            null
                        }
                    }
                    else -> null
                }
                Row(Modifier.gap(Space.MD)) {
                    approvalNote?.let { note ->
                        Link(path = "/app/settings/agents/approvals") { Text(note) }
                    }
                    Link(path = "/app/settings/agents/setup/claude") { Text(t("app.settings.agents.client_setup", "Client setup")) }
                }

                LAsyncContent(
                    state,
                    rows = 2,
                    loadingLabel = t("app.settings.agents.loading_agents", "Loading agents"),
                    onRetry = { refreshKey++ },
                ) { agents ->
                    if (agents.isEmpty()) {
                        LEmptyState(
                            t("app.settings.agents.no_agents_connected", "No agents connected"),
                            t("app.settings.agents.an_agent_can_read_your_strings_2", "An agent can read your strings, propose translations and run QA — with a " +
                                "credit budget you set, which defaults to zero. It is how a bot, a CI job " +
                                "or an MCP client works here without borrowing somebody's password."),
                            Modifier.fillMaxWidth(),
                            LEmptyStateIcon.AUTOMATION,
                        ) {
                            // The primary, because while this state is on screen it is the whole
                            // page: an empty state invites one action, and the header has stood
                            // down for exactly as long as this is drawn.
                            LButton(
                                t("app.settings.agents.connect_an_agent", "Connect an agent"),
                                { ctx.router.navigateTo("/app/settings/agents/new") },
                                leadingIcon = { LPlusIcon() },
                            )
                        }
                    } else {
                        // The rows and the card under them share one wrapper, so the space between
                        // them is set here — the page column's gap falls outside it.
                        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                            // Sorted by last used so the noisy one is at the top — that is the one
                            // somebody opening this page is usually here about.
                            agents
                                .sortedByDescending { it.lastUsedAt ?: "" }
                                .forEachIndexed { index, agent -> AgentCard(agent, index) }

                            LCard(Modifier.fillMaxWidth()) {
                                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                                    LSectionTitle(t("app.settings.agents.stop_everything", "Stop everything"))
                                    PauseAllAgentsAction(
                                        pageClaim = agentClaim,
                                        agentCount = agents.size,
                                        confirming = confirmingPauseAll,
                                        onConfirmingChange = { confirmingPauseAll = it },
                                        busy = busy,
                                        setBusy = { busy = it },
                                        onPaused = {
                                            confirmingPauseAll = false
                                            refreshKey++
                                            message = null
                                        },
                                        onFailure = { error ->
                                            message = error.message?.takeIf(String::isNotBlank)
                                                ?: agentPauseRefused()
                                        },
                                    )
                                    // The thing an owner reaches for at 2am, and it must not read as
                                    // revocation: nothing is destroyed and one click each puts them
                                    // back.
                                    // One literal, not a run joined by `+`: the extractor only reaches a
                                    // single-literal `t(…)`, so a sentence split across `+` never
                                    // reached the catalogue and could not be translated at all.
                                    LMuted(t("app.settings.agents.every_connection_stops_answering_nothing_is_2", "Every connection stops answering. Nothing is destroyed — scopes, budgets and project lists all survive, and each one resumes in a click."))
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun PauseAllAgentsAction(
    pageClaim: WorkspaceActionClaim,
    agentCount: Int,
    confirming: Boolean,
    onConfirmingChange: (Boolean) -> Unit,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    pauseAll: suspend (workspaceSlug: String) -> Unit = ApiClient::pauseAllAgents,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    onPaused: () -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }

    LButton(
        t("app.settings.agents.pause_all_agents", "Pause all agents"),
        { onConfirmingChange(true) },
        Modifier.fillMaxWidth(),
        variant = LButtonVariant.SECONDARY,
        enabled = !busy,
    )
    // Arming and firing used to be the same button, so a double-click stopped every agent in the
    // workspace and the armed label had no way back short of a reload. A dialog gives the second
    // click somewhere to land that is not "yes", and states the consequence where it is being
    // decided rather than on the card behind it.
    LConfirmDialog(
        open = confirming,
        title = t("app.settings.agents.pause_every_agent", "Pause every agent?"),
        // Counted, not interpolated: a Kotlin template is invisible to the catalogue, so the half
        // of this control that says "yes" would have stayed English while its trigger translated.
        confirmLabel = tPlural(
            "app.settings.agents.yes_pause_all",
            agentCount,
            "Yes, pause all {count}",
            "Yes, pause all {count}",
        ),
        onConfirm = {
            pageClaim
                .takeIf { it.remainsAuthorized(currentContext()) }
                ?.let { actionClaim ->
                    launch {
                        runWorkspaceAction(
                            claim = actionClaim,
                            currentContext = currentContext,
                            setBusy = setBusy,
                            transport = pauseAll,
                            onSuccess = {
                                onConfirmingChange(false)
                                onPaused()
                            },
                            // Closed on failure too: the page's own error banner sits behind the
                            // scrim, so leaving the dialog up hides the only account of what went
                            // wrong.
                            onFailure = { failure ->
                                onConfirmingChange(false)
                                onFailure(failure)
                            },
                        )
                    }
                }
        },
        onCancel = { onConfirmingChange(false) },
        tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.settings.agents.nothing_is_destroyed", "Nothing is destroyed"),
        consequence = t("app.settings.agents.every_connection_stops_answering_scopes_budgets", "Every connection stops answering. Scopes, budgets and project lists all survive, and each one resumes in a click."),
        busy = busy,
    )
}

@Composable
private fun AgentCard(
    agent: AgentConnectionDto,
    /** Position in the list, so the fleet assembles rather than appearing as one block. */
    index: Int = 0,
) {
    val ctx = rememberPageContext()
    LCard(Modifier.fillMaxWidth().enterStaggered(index)) {
        Column(Modifier.fillMaxWidth().gap(Space.XS)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                LSectionTitle(agent.name, Modifier.flexGrow(1))
                if (agent.paused) LBadge(t("app.settings.agents.paused", "paused"), variant = LBadgeVariant.WARNING)
                LBadge(agent.autonomy.id)
                LButton(
                    t("app.settings.agents.open", "Open"),
                    { ctx.router.navigateTo("/app/settings/agents/${agent.id}") },
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    // A fleet of eight rows announces "Open" eight times otherwise, and the visible
                    // column gives a screen reader nothing to tell them apart.
                    accessibleLabel = agentRowOpenLabel(agent.name),
                )
            }
            // Self-reported by the client. Rendered as escaped text, never markup, and never
            // interpolated anywhere — it is a string an agent chooses for itself.
            agent.clientName?.let { LMuted(it) }
            LMuted(agent.scopes.joinToString(", ") { it.id })
            LMuted(
                if (agent.projectSlugs.isEmpty()) {
                    t("app.settings.agents.every_project_in_this_workspace", "Every project in this workspace.")
                } else {
                    trf(
                        "app.settings.agents.only_projects",
                        "Only: {projects}",
                        "projects" to agent.projectSlugs.joinToString(", "),
                    )
                },
            )
            // The thing most likely to have changed underneath somebody: an agent set up by an admin
            // who has since become a translator can no longer approve, and this says so before
            // anybody spends an afternoon on it.
            agent.grantingRole?.let { role ->
                LMuted(tf(
                    "app.settings.agents.borrows_authority_of_role",
                    "Borrows the authority of a {role}, as they are right now.",
                    "role" to role.name.lowercase(),
                ))
            } ?: LMuted(t("app.settings.agents.the_member_who_created_this_has", "The member who created this has left the workspace, so it no longer works."))
            if (TokenScope.MT_SPEND in agent.scopes && agent.creditBudgetMonthly > 0) {
                P(Modifier.tabularNumbers().fontSize(TypeScale.Small).margin(0.px).toAttrs()) {
                    Text(
                        // One sentence rather than three ceilings and two commas: the list
                        // separators belong to the language, not to Kotlin.
                        trf(
                            "app.settings.agents.budget_ceilings",
                            "Up to {perCall} credits a call, {perDay} a day, {perMonth} a month.",
                            "perCall" to agent.maxCreditsPerCall,
                            "perDay" to agent.creditBudgetDaily,
                            "perMonth" to agent.creditBudgetMonthly,
                        ),
                    )
                }
            } else {
                LMuted(t("app.settings.agents.cannot_spend_credits", "Cannot spend credits."))
            }
            LMuted(
                agent.lastUsedAt?.let {
                    trf(
                        "app.settings.agents.last_used_date",
                        "Last used {date}.",
                        "date" to it.take(10),
                    )
                } ?: t("app.settings.agents.never_used_yet", "Never used yet."),
            )
            LMuted(tf("app.settings.agents.expires_on", "Expires {date}.", "date" to agent.expiresAt.take(10)))
        }
    }
}

/**
 * What one connection's Open button is called to a screen reader.
 *
 * `"$openLabel ${agent.name}"` translated the verb and left the join in English word order, so a
 * German reader heard "Öffnen release-bot" with no way for a locale that needs the object first —
 * or a particle between the two — to say so, and the template was invisible to the extractor's
 * parameter check. One catalogued frame with a named placeholder instead.
 */
internal fun agentRowOpenLabel(name: String): String = trf(
    "app.settings.agents.open_name",
    "Open {name}",
    "name" to name,
)

/**
 * What the pause-everything control says when it fails.
 *
 * The 2am control on this page: its dialog title, label and consequence were all catalogued and the
 * one sentence a reader only ever meets because the pause did not happen was not. `tr`, because
 * this is a failure handler.
 */
internal fun agentPauseRefused(): String = tr(
    "app.settings.agents.couldnt_pause_these_agents_retry",
    "Couldn't pause these agents. Retry?",
)
